import {
  useRef,
  useCallback,
  useSyncExternalStore,
  useEffect,
  useMemo,
} from 'react';

const inMemoryData = new Map();
const callbacks = new Set();

const triggerCallbacks = (key) => {
  for (const callback of [...callbacks]) {
    callback(key);
  }
};

const parseJSON = (value) =>
  value === 'undefined' ? undefined : JSON.parse(value);

const goodTry = (tryFn) => {
  try {
    return tryFn();
  } catch {
    /* empty */
  }
};

export const useLocalStorage = (
  key,
  options,
) => {
  const defaultOptions = useMemo(
    () => ({
      defaultValue: undefined,
      defaultServerValue: undefined,
      storageSync: true,
      parse: parseJSON,
      stringify: JSON.stringify,
      ...options,
    }),
    [options],
  );

  const storageItem = useRef({
    string: null,
    parsed: undefined,
  });

  const storeChangeSubscribe = useCallback(
    (onStoreChange) => {
      const onChange = (localKey) => {
        if (key === localKey) {
          onStoreChange();
        }
      };

      callbacks.add(onChange);

      return () => {
        callbacks.delete(onChange);
      };
    },
    [key],
  );

  const storeChangeGetSnapshot = useCallback(() => {
    const string = goodTry(() => localStorage.getItem(key)) ?? null;

    if (inMemoryData.has(key)) {
      storageItem.current.parsed = inMemoryData.get(key)
    } else if (string !== storageItem.current.string) {
      let parsed = undefined;

      try {
        if (string === null) {
          parsed = defaultOptions.defaultValue
        } else {
          const valueParser = defaultOptions.stringify?.(string) ?? '';

          parsed = defaultOptions.parse?.(valueParser)
        }
      } catch {
        parsed = defaultOptions.defaultValue;
      }

      storageItem.current.parsed = parsed;
    }

    storageItem.current.string = string;

    if (defaultOptions.defaultValue !== undefined && string === null) {
      goodTry(() => {
        const string =
          defaultOptions.stringify?.(defaultOptions.defaultValue) ?? '';

        localStorage.setItem(key, string);

        storageItem.current = { string, parsed: defaultOptions.defaultValue };
      });
    }

    return storageItem.current.parsed;
  }, [defaultOptions, key]);

  const storeChangeGetServerSnapshot = useCallback(
    () =>
      defaultOptions.defaultServerValue ?? defaultOptions.defaultValue,
    [defaultOptions.defaultServerValue, defaultOptions.defaultValue],
  );

  const value = useSyncExternalStore(
    storeChangeSubscribe,
    storeChangeGetSnapshot,
    storeChangeGetServerSnapshot,
  );

  const set = useCallback(
    (newValue) => {
      const value =
        newValue instanceof Function
          ? newValue(storageItem.current.parsed)
          : newValue;

      try {
        localStorage.setItem(key, defaultOptions.stringify?.(value) ?? '');

        inMemoryData.delete(key);
      } catch {
        inMemoryData.set(key, value);
      }

      triggerCallbacks(key);
    },
    [defaultOptions, key],
  );

  const remove = useCallback(() => {
    goodTry(() => localStorage.removeItem(key));

    inMemoryData.delete(key);

    triggerCallbacks(key);
  }, [key]);

  const isPersistent = useMemo(
    () => value === defaultOptions.defaultValue || !inMemoryData.has(key),
    [defaultOptions.defaultValue, key, value],
  );

  useEffect(() => {
    if (!defaultOptions.storageSync) {
      return undefined;
    }

    const onStorage = (e) => {
      if (e.key === key && e.storageArea === goodTry(() => localStorage)) {
        triggerCallbacks(key);
      }
    };

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, [key, defaultOptions.storageSync]);

  return { value, set, remove, isPersistent };
};
